import { useCallback, useEffect, useState, useRef } from "react";
import { Wrapper, Image, Icon } from "nystem-components";

const formats = ["maxres", "sd", "hq", "mq"];

const youtubeImageFormat = (value, format) => {
  const valArr = (value || "").replace(/default_live\.jpg(?=\?|$)/, "default.jpg").split("/");
  valArr[valArr.length - 1] = format + valArr[valArr.length - 1];
  return valArr.join("/");
};

const getRegionSharpness = (gray, width, height, fromX, toX) => {
  let total = 0;
  let count = 0;

  for (let y = 1; y < height - 1; y++) {
    for (let x = fromX; x < toX; x++) {
      const pos = y * width + x;
      total += Math.abs(gray[pos + 1] - gray[pos - 1]) + Math.abs(gray[pos + width] - gray[pos - width]);
      count++;
    }
  }

  return total / count;
};

const isVerticalYoutubeThumbnail = (image) => {
  if (image.naturalWidth === 120 || image.naturalHeight > image.naturalWidth) return true;

  const width = 160;
  const height = 90;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d", { willReadFrequently: true });
  context.drawImage(image, 0, 0, width, height);

  const { data } = context.getImageData(0, 0, width, height);
  const gray = new Float32Array(width * height);

  for (let index = 0; index < gray.length; index++) {
    const pos = index * 4;
    gray[index] = data[pos] * 0.299 + data[pos + 1] * 0.587 + data[pos + 2] * 0.114;
  }

  const left = getRegionSharpness(gray, width, height, 0, 45);
  const center = getRegionSharpness(gray, width, height, 58, 102);
  const right = getRegionSharpness(gray, width, height, 115, 160);
  const side = (left + right) / 2;

  return center > 20 && side < 12 && center / side > 3.4;
};

const UrlImageYoutube = ({ value, model, view }) => {
  const [at, setAt] = useState(0);
  const [allowVerticalFallback, setAllowVerticalFallback] = useState(false);
  const youtube = /^https?:\/\/(?:i|img)\.ytimg\.com\//.test(value || "");
  const url = youtube ? youtubeImageFormat(value, formats[at]) : value;
  const ref = useRef();
  const verticalFallbackAt = useRef(false);
  const useNextFormat = useCallback(() => {
    setAt((current) => {
      if (current < formats.length - 1) return current + 1;
      if (verticalFallbackAt.current !== false) {
        setAllowVerticalFallback(true);
        return verticalFallbackAt.current;
      }
      return 100;
    });
  }, []);

  useEffect(() => {
    setAt(0);
    setAllowVerticalFallback(false);
    verticalFallbackAt.current = false;
  }, [value]);

  useEffect(() => {
    if (!ref.current || !youtube) return;

    const image = ref.current;

    image.onerror = () => {
      console.log("error", view.value);
      useNextFormat();
    };
    image.onload = () => {
      if (allowVerticalFallback) return;

      if (isVerticalYoutubeThumbnail(image)) {
        if (verticalFallbackAt.current === false) verticalFallbackAt.current = at;
        useNextFormat();
      }
    };

    if (!allowVerticalFallback && image.complete && image.naturalWidth && isVerticalYoutubeThumbnail(image)) {
      if (verticalFallbackAt.current === false) verticalFallbackAt.current = at;
      useNextFormat();
    }
    return () => {
      image.onerror = null;
      image.onload = null;
    };
  }, [allowVerticalFallback, at, url, useNextFormat, view.value, youtube]);

  if (!value || (youtube && at === 100))
    return (
      <Wrapper className={model.classNameEmpty}>
        <Icon icon="brands-youtube" style={{ width: "100%" }} />
      </Wrapper>
    );

  return (
    <Image
      ref={ref}
      className={model.className}
      crossOrigin={youtube ? "anonymous" : undefined}
      src={url}
      width={model.width}
      draggable={!model.notDraggable}
    />
  );
};

export default UrlImageYoutube;
